import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Navbar } from '@lunarcrush/ds/marketing';
import { Text } from '@lunarcrush/ds';
import { marketing } from '@lunarcrush/ds/tokens';
import { useCMS } from '../cms';
import { parseJSON } from '../cms/helpers';
import { useAppNavigation } from '../hooks/useAppNavigation';
import MobileNav from '../MobileNav';
import { PlatformMegaMenu } from '../PlatformMegaMenu';
import { LanguagePicker } from '../LanguagePicker';
import { ThemePicker } from '../ThemePicker';
import { NavSearchButton } from '../NavSearchButton';
import JsonLd from '../components/JsonLd';
import { DeferredSection } from '../components/DeferredSection';
import { ORGANIZATION_SCHEMA, WEB_APPLICATION_SCHEMA } from '../lib/structured-data';
import { logoWidth } from '../lib/logo-aspect';
import { HeroDiscover } from '../components/HeroDiscover';

// DEV-12444 / DEV-12641: below-fold sections must not parse on the hero chunk.
// DeferredSection skips the commit, but a static FeatureCard/PricingCard import
// still lands on the HomePage parse path and the 3.5s bench window.
const HomeBelowFold = React.lazy(() => import('./HomeBelowFold'));

type CMSTrustLogo = { name: string; logo: string; height: number; url?: string };

// DEV-12658: optical alignment for the trust row. Centring boxes misaligns the
// wordmarks: each SVG has its own descender and icon space (Google's "g",
// OpenAI's blossom). This is where the text baseline sits in each asset, as a
// fraction of its height, measured from the rendered ink. Heights in the CMS
// equalise cap/x-height; the row aligns bottoms and each logo shifts down by
// the space under its baseline, so every baseline lands on one line.
const TRUST_LOGO_BASELINE: Record<string, number> = {
  '/landing-assets/anthropic.svg': 0.98,
  '/landing-assets/openai.svg': 0.68,
  '/landing-assets/coinbase.svg': 0.99,
  '/landing-assets/google.svg': 0.75,
  '/landing-assets/tradingview.svg': 0.76,
};

const useTrustLogos = (): CMSTrustLogo[] => {
  const { pages } = useCMS();
  return parseJSON(pages.homepage.hero_trust_logos, []);
};

const HomePage: React.FC = () => {
  const navProps = useAppNavigation();
  const location = useLocation();
  const { pages } = useCMS();

  // Handle scroll-to-section when navigating back from other pages
  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (scrollTo) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      // Clear state so refresh doesn't re-scroll
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  return (
    <>
      {/* ─── Structured data (DEV-10847) ──── */}
      <JsonLd id="organization" schema={ORGANIZATION_SCHEMA} />
      <JsonLd id="webapplication" schema={WEB_APPLICATION_SCHEMA} />

      {/* ─── Sticky Nav ──── */}
      <div className="sticky-nav desktop-nav">
        <Navbar
          logo={
            <img
              src="/landing-assets/lunarCrush-header-logo.png"
              alt="LunarCrush"
              width={140}
              height={32}
              style={{ objectFit: 'contain' }}
            />
          }
          {...navProps}
          extras={<><NavSearchButton /><ThemePicker /><LanguagePicker /></>}
        />
        <PlatformMegaMenu />
      </div>
      <div className="sticky-nav mobile-nav">
        <MobileNav />
      </div>

      {/* ─── Hero (DEV-12658: V3 open discovery field) ──── */}
      <section className="hdv-hero">
        <HeroDiscover
          eyebrow={pages.homepage.hero_eyebrow || 'Explore what\u2019s happening'}
          headline={pages.homepage.hero_headline}
          subtitle={pages.homepage.hero_subtitle}
          ctaLabel={pages.homepage.hero_cta_label || 'GET STARTED'}
          ctaPath={pages.homepage.hero_cta_path || '/login'}
        />
        {(() => {
          const trustLogos = useTrustLogos();
          return (
            <div className="trust-logos-wrap hdv-trust" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
              <Text size={12} weight="500" color={marketing.textSubtle} fontFamily="Plus Jakarta Sans" style={{ letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {pages.homepage.hero_trust_label || pages.homepage.trust_label}
              </Text>
              <div className="trust-logos" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 40, opacity: 0.5 }}>
                {trustLogos.map((logo) => {
                  // DEV-12095 established explicit dimensions here. DEV-12178
                  // moved the ratios into `logo-aspect.ts`, generated from each
                  // SVG's own viewBox, so /enterprise could reuse them instead of
                  // growing a second copy. The four values are unchanged.
                  //
                  // The map this replaced was keyed by logo NAME, covered 4 of the
                  // 10 logo assets, and fell back to a ratio of 4 — so any logo
                  // added to the content got a confidently WRONG width. `logoWidth`
                  // returns undefined for an asset it has no ratio for, which
                  // degrades to no reservation rather than a wrong one.
                  const width = logoWidth(logo.logo, logo.height);
                  const baseline = TRUST_LOGO_BASELINE[logo.logo];
                  const baselineShift = baseline === undefined ? undefined : `translateY(${((1 - baseline) * 100).toFixed(1)}%)`;

                  const img = (
                    <img
                      src={logo.logo}
                      alt={logo.name}
                      data-logo={logo.name.toLowerCase()}
                      {...(width ? { width } : {})}
                      height={logo.height}
                      className="trust-logo-img"
                      loading="lazy"
                      decoding="async"
                      style={{ objectFit: 'contain', ...(baselineShift ? { transform: baselineShift } : {}) }}
                    />
                  );
                  return logo.url ? (
                    <a key={logo.name} href={logo.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {img}
                    </a>
                  ) : (
                    <span key={logo.name} style={{ display: 'inline-flex', alignItems: 'center' }}>{img}</span>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </section>

      {/* DEV-12330 / DEV-12641: below-the-fold parse AND commit stay off the
          hero chunk. Lazy HomeBelowFold so FeatureCard/PricingCard are not on
          the HomePage parse path; DeferredSection so the import() itself waits
          for scroll or the 4s blocking window. Suspense (sized fallback) is
          inside DeferredSection so the 3760px reservation never drops. */}
      <DeferredSection minHeight={3760}>
        <HomeBelowFold />
      </DeferredSection>
    </>
  );
};

export default HomePage;
